import requests from './httpServices';

/**
 * Attempts to capture a precise GPS location via the browser
 * Geolocation API. Non-blocking — resolves with a structured result
 * regardless of whether the user grants or denies the permission so
 * callers can always include `preciseLocation` metadata in an audit
 * entry without special-casing.
 *
 * Returns one of:
 *   { status: 'granted', latitude, longitude, accuracy, altitude, timestamp }
 *   { status: 'denied', reason }
 *   { status: 'unsupported' }
 *   { status: 'timeout' }
 *   { status: 'error', reason }
 */
export function capturePreciseLocation({ timeoutMs = 6000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ status: 'unsupported' });
      return;
    }
    let settled = false;
    const safetyTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ status: 'timeout' });
    }, timeoutMs + 500);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(safetyTimer);
        const { latitude, longitude, accuracy, altitude } = pos.coords || {};
        resolve({
          status: 'granted',
          latitude: typeof latitude === 'number' ? Number(latitude.toFixed(7)) : null,
          longitude: typeof longitude === 'number' ? Number(longitude.toFixed(7)) : null,
          accuracy: accuracy != null ? Math.round(accuracy) : null,
          altitude: altitude != null ? Number(altitude.toFixed(1)) : null,
          timestamp: pos.timestamp || Date.now(),
        });
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(safetyTimer);
        const codeMap = {
          1: 'permission_denied',
          2: 'position_unavailable',
          3: 'timeout',
        };
        resolve({
          status: err?.code === 1 ? 'denied' : 'error',
          reason: codeMap[err?.code] || err?.message || 'unknown',
        });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

/**
 * Fire-and-forget audit event append. Never throws — audit capture failures
 * must never break the contract UX flow. Returns the promise so callers
 * can still await it if they want.
 */
export function logContractAuditEvent({
  contractElementId,
  meetingId,
  eventType,
  actor,
  metadata,
  documentVersion,
}) {
  if (!contractElementId || !eventType) {
    return Promise.resolve({ skipped: true });
  }
  return requests
    .post('/contract/audit/event', {
      contractElementId,
      meetingId: meetingId || '',
      eventType,
      actor: actor || {},
      metadata: metadata || {},
      documentVersion: documentVersion || '',
    })
    .catch((err) => {
      console.warn('[ContractAudit] log failed', err?.message || err);
      return { error: true };
    });
}

export function downloadContractCertificate({
  contractElementId,
  contractName,
  participantId,
  participantName,
}) {
  if (!contractElementId) return;
  const base = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  const params = new URLSearchParams();
  if (contractName) params.set('name', contractName);
  if (participantId) params.set('participantId', participantId);
  if (participantName) params.set('participantName', participantName);
  const qs = params.toString();
  const url = `${base}/contract/audit/${encodeURIComponent(
    contractElementId
  )}/certificate.pdf${qs ? '?' + qs : ''}`;
  window.open(url, '_blank', 'noopener');
}
